/**
 * Typed content loader — the ONLY place the app touches content JSON.
 *
 * Requires in tsconfig.json:
 *   "resolveJsonModule": true,
 *   "esModuleInterop": true
 *
 * Adjust the relative import paths (or add a path alias like "@content/*")
 * to match the repository layout once dropped in.
 */

import type {
	AchievementEntry,
	Appreciation,
	Award,
	Certification,
	ContentFile,
	EducationEntry,
	ExperienceEntry,
	MartialArtsDiscipline,
	Metric,
	MetricDerivation,
	Product,
	Profile,
	Project,
	ResolvedMetric,
	Service,
	SkillsData,
	SocialLink,
	Story,
	Workshop
} from "../types/content";
import { isPublishableCertification } from "../types/content";

import achievementsJson from "./data/achievements.json";
import appreciationsJson from "./data/appreciations.json";
import awardsJson from "./data/awards.json";
import certificationsJson from "./data/certifications.json";
import educationJson from "./data/education.json";
import experienceJson from "./data/experience.json";
import martialArtsJson from "./data/martialArts.json";
import metricsJson from "./data/metrics.json";
import productsJson from "./data/products.json";
import profileJson from "./data/profile.json";
import projectsJson from "./data/projects.json";
import servicesJson from "./data/services.json";
import skillsJson from "./data/skills.json";
import storiesJson from "./data/stories.json";
import workshopsJson from "./data/workshops.json";

/* ------------------------------------------------------------------------ */
/* Derived values — computed from the layer, never typed into it             */
/* ------------------------------------------------------------------------ */

const experienceEntries: ExperienceEntry[] = (experienceJson as ContentFile<ExperienceEntry[]>).data;

/**
 * Years of professional experience, floored, as a display string: "8+".
 *
 * COMPUTED FROM THE EARLIEST ROLE, NEVER TYPED (D-071). The layer used to carry
 * "8+" in three places while its own earliest `startDate` is 2018-09 — 7 years
 * 11 months on 2026-08-21 — so the site rounded a year up and would have needed
 * a hand edit every September merely to stay true. Both halves of the sum now
 * come from files that already exist: the start date from experience.json, and
 * "now" from `__CONTENT_AS_OF__`, HEAD's commit date stamped in at build time.
 *
 * The consequence of using a build constant rather than a clock is worth
 * stating plainly: this figure increments on the first DEPLOY after the
 * anniversary, not at midnight on it. That is the honest reading of a static
 * artifact — the page says what was true when it was built — and it is the only
 * version that cannot produce a hydration mismatch (see declaraction.d.ts).
 */
export const yearsExperience: string = (() => {
	const earliest: string = experienceEntries.reduce(
		(soFar: string, entry: ExperienceEntry) => (entry.startDate < soFar ? entry.startDate : soFar),
		experienceEntries[0].startDate
	);

	/* THE ANCHOR MAY BE EARLIER THAN THE EARLIEST ROLE, AND ONLY EARLIER (D-080).
	   `meta.careerStartDate` records a part-time beginning that predates the
	   full-time start on the role card. The owner ruled on 2026-08-24 that the
	   card keeps its September date and the count starts in July, which is a
	   number and a visible timeline that appear to disagree — the D-066 shape.
	   Two things keep it honest and both are content, not code: the band tile's
	   own context line names the anchor month, and the role summary carries both
	   dates. If either is edited away, the page asserts a figure nothing on it
	   supports.

	   The guards are the interesting part. An anchor LATER than the earliest
	   role would be a way to inflate a headline number from a file no reviewer
	   reads, so it throws; an anchor with no note throws too, because a bare
	   date here is indistinguishable from a typo. Both fail at module load,
	   which fails the build, because the pre-render imports this module. */
	const meta = (experienceJson as ContentFile<ExperienceEntry[]>).meta;
	const anchor: string | undefined = meta.careerStartDate;
	if (anchor !== undefined) {
		if (!/^\d{4}-\d{2}$/.test(anchor)) {
			throw new Error(`experience.json: careerStartDate "${anchor}" is not YYYY-MM.`);
		}
		if (anchor > earliest) {
			throw new Error(
				`experience.json: careerStartDate "${anchor}" is LATER than the earliest role "${earliest}". ` +
					"The anchor may only move the count back, never forward — delete it, or correct the role's startDate."
			);
		}
		if (!meta.careerStartNote || meta.careerStartNote.length < 40) {
			throw new Error(
				"experience.json: careerStartDate is set without a substantive careerStartNote. " +
					"Record why the timeline starts before the earliest role, or remove the anchor."
			);
		}
	}

	const [startYear, startMonth] = (anchor ?? earliest).split("-").map(Number);
	const [nowYear, nowMonth] = __CONTENT_AS_OF__.slice(0, 7).split("-").map(Number);
	const months: number = (nowYear - startYear) * 12 + (nowMonth - startMonth);
	if (!Number.isFinite(months) || months < 0) {
		throw new Error(
			`content: cannot derive years of experience from earliest start "${earliest}" and content date "${__CONTENT_AS_OF__}".`
		);
	}
	return `${Math.floor(months / 12)}+`;
})();

/**
 * Resolves `{{token}}` placeholders in narrative content.
 *
 * `profile.summary` is prose synced from the master resume and it states a year
 * count, which D-071 ruled must not be typed anywhere. Templating it keeps the
 * sentence intact and canonical while the number stays derived.
 *
 * THE RESOLUTION HAPPENS HERE, IN THE LOADER, SO NO CONSUMER CAN SEE A RAW
 * TOKEN. `personJsonLd` puts this string in front of a search engine and
 * `biography.ts` puts its first two sentences in the hero; a literal
 * "{{yearsExperience}}" reaching either is a defect no type could catch, so an
 * unknown token throws and fails the build instead of shipping.
 */
const fillTokens = (text: string): string => {
	const filled = text.replace(/\{\{yearsExperience\}\}/g, yearsExperience);
	const unresolved = filled.match(/\{\{[^}]*\}\}/g);
	if (unresolved) {
		throw new Error(`content: unresolved template token(s) ${unresolved.join(", ")} in a content string.`);
	}
	return filled;
};

const profileRaw: Profile = (profileJson as ContentFile<Profile>).data;

export const profile: Profile = { ...profileRaw, summary: fillTokens(profileRaw.summary) };

/**
 * Canonical URL for a `profile.socialLinks` entry, or "" when absent.
 *
 * Exists so that no component ever writes a profile URL as a literal. Before
 * this, the Credly link was hardcoded in three components and a comment, all
 * four carrying a slug that disagreed with `profile.json` — the class of drift
 * Article II.2 forbids. Change the URL in `profile.json`; every consumer
 * follows.
 *
 * Returns "" rather than throwing: a missing social link is a content gap, not
 * a crash, and an empty `href` is inert.
 */
export const socialUrl = (id: string): string =>
	profile.socialLinks.find((link: SocialLink) => link.id === id)?.url ?? "";

export const experience: ExperienceEntry[] = experienceEntries;

export const education: EducationEntry[] = (educationJson as ContentFile<EducationEntry[]>).data;

/** Full certification list, including entries still pending verification. */
export const certificationsAll: Certification[] = (certificationsJson as ContentFile<Certification[]>).data;

/** Publish-safe list — pending-details entries are excluded by design. */
export const certifications: Certification[] = certificationsAll.filter(isPublishableCertification);

export const skills: SkillsData = (skillsJson as ContentFile<SkillsData>).data;

export const awards: Award[] = (awardsJson as ContentFile<Award[]>).data;

/**
 * The owned SFT product suite — the /products route.
 *
 * Split from platforms.json on 2026-08-20 (D-067), which held all 28 rows and
 * fed a single Home section. TWO FILES RATHER THAN ONE FILE WITH A FLAG: each
 * page then reaches only its own data, and reachability is what decides what
 * ships (the CONTEXT 7b correction under D-066). A discriminated single array
 * would put all 28 entries in the bundle for both routes.
 */
export const products: Product[] = (productsJson as ContentFile<Product[]>).data;

/** Client and delivery builds outside the product suite — the /projects route. */
export const projects: Project[] = (projectsJson as ContentFile<Project[]>).data;

/**
 * Offered capabilities — what the author does, as distinct from `products` and
 * `projects`, which record what was built. Consumed directly by the Services section
 * (no bridge, Constitution V.4).
 */
export const services: Service[] = (servicesJson as ContentFile<Service[]>).data;

/**
 * Resolves one metric to a display-ready record, and refuses the two shapes
 * that would otherwise ship quietly.
 *
 * THIS IS THE ONLY PLACE THE METRIC INVARIANTS ARE ACTUALLY ENFORCED. The
 * `Metric` union describes them, but the JSON arrives through a type assertion,
 * which suppresses the check that would have caught a violation — proven by
 * planting one and watching `tsc` pass. So the three failures below throw here,
 * at module load, which fails `npm run build` because the pre-render imports
 * this module:
 *
 *   - both a typed value and a derivation: one of the two is stale, and
 *     silently preferring either is how a superseded literal survives;
 *   - neither: the tile would render blank;
 *   - not featured and no note: a publishable-looking claim with nothing
 *     recording why it does not appear, which is the D-065 shape exactly.
 */
const resolveMetric = (metric: Metric): ResolvedMetric => {
	/* Widened on purpose. `Metric` is a union whose two arms declare the other's
	   field as `never`, so testing "are BOTH present" narrows the union to
	   `never` and every property access inside the branch stops compiling. The
	   union describes the legal shapes; this function has to be able to look at
	   an ILLEGAL one in order to reject it, which means reading the record as
	   the JSON actually presents it. */
	const entry: {
		id: string;
		label: string;
		context: string;
		featured: boolean;
		note?: string;
		value?: string;
		derivation?: MetricDerivation;
	} = metric;
	const { derivation, value, ...rest } = entry;

	if (derivation !== undefined && value !== undefined) {
		throw new Error(
			`metrics.json: "${entry.id}" carries both a typed value ("${value}") and the derivation "${derivation}". One of them is stale — delete it.`
		);
	}
	if (!entry.featured && !entry.note) {
		throw new Error(
			`metrics.json: "${entry.id}" is not featured and carries no note. Record why it is withheld, or feature it.`
		);
	}
	if (derivation === "years-since-first-role") {
		return { ...rest, value: yearsExperience };
	}
	if (typeof value === "string" && value.length > 0) {
		return { ...rest, value };
	}
	throw new Error(`metrics.json: "${entry.id}" has neither a value nor a known derivation.`);
};

export const metrics: ResolvedMetric[] = (metricsJson as ContentFile<Metric[]>).data.map(resolveMetric);

/**
 * The Home metrics band's headline figures — curated by owner ruling
 * 2026-08-21 (D-071) via the `featured` flag, so the layer decides what leads
 * and the component curates nothing. `/dashboard` reads this same list for the
 * same reason (D-094): a second consumer that re-curated would be a second
 * opinion about an owner ruling.
 *
 * NO COUNT IS STATED HERE, AND THE ABSENCE IS THE POINT (D-094). This comment
 * used to say "four headline figures" and "the six unfeatured entries", both of
 * which were true when D-071 wrote them and both of which D-072 falsified the
 * following day by promoting the four percentages — leaving 8 featured and 2
 * unfeatured under a comment asserting 4 and 6. A number in a comment is a
 * claim nothing checks, which is D-066's rule applied to source: if the layer
 * can settle it, do not restate it.
 *
 * The unfeatured entries are NOT dead: each carries a `note` saying why it is
 * withheld and where it still ships.
 */
export const featuredMetrics: ResolvedMetric[] = metrics.filter((m: ResolvedMetric) => m.featured);

/** Full Career Stories collection — every story renders on /stories. */
export const stories: Story[] = (storiesJson as ContentFile<Story[]>).data;

/** Stories for the Home highlight strip. */
export const featuredStories: Story[] = stories.filter((s: Story) => s.featured);

/**
 * Karate, Muay Thai and the NCC — the /martial-arts route's whole source.
 *
 * Consumed directly by the page (no bridge, V.4). The teaser on `/` reads the
 * same export and picks three records BY ID in its own module, because which
 * three lead is an editorial choice and D-065 settled that those belong in
 * presentation, not in the layer.
 */
export const martialArts: MartialArtsDiscipline[] = (martialArtsJson as ContentFile<MartialArtsDiscipline[]>).data;

/**
 * External recognition — the /achievements route's whole source (D-069).
 *
 * Consumed directly by the page (no bridge, V.4). The teaser on `/` reads the
 * same export. Every entry's `attribution` is load-bearing: the page renders a
 * team feature and a personal one differently, because writing "featured" over
 * a photograph of three people when only the company was named is the kind of
 * over-claim this documentary exists not to make.
 */
export const achievements: AchievementEntry[] = (achievementsJson as ContentFile<AchievementEntry[]>).data;

/**
 * Workshops delivered and training conducted — the /workshops route (D-076).
 *
 * SORTED HERE, NEWEST FIRST, RATHER THAN TRUSTED FROM FILE ORDER. The owner's
 * brief specified latest-to-oldest, and a JSON array carries that only as a
 * convention nothing enforces — the next entry appended to the end of the file
 * would silently land at the bottom of the page whatever its date. Deriving the
 * order from `date` makes the requirement structural: an entry cannot be
 * mis-ordered, because nothing chooses its position.
 *
 * `date` is "YYYY-MM" or "YYYY", so a plain string comparison IS a chronological
 * one; no Date parsing, and therefore none of the timezone hazard `formatMonth`
 * in constants/experience.ts documents (`new Date("2020-03")` is read as UTC and
 * renders as February west of Greenwich). A year-only entry sorts to the start
 * of its year, which is the honest position for a date known only that far.
 */
export const workshops: Workshop[] = [...(workshopsJson as ContentFile<Workshop[]>).data].sort(
	(a: Workshop, b: Workshop) => b.date.localeCompare(a.date)
);

/* --------------------------------------------------------------------------
   EVERY SLUG IS A ROUTE, SO EVERY SLUG IS CHECKED HERE (D-081).
   --------------------------------------------------------------------------
   `slug` is not an ordinary string field: `scripts/prerender.mjs` turns each
   one into a URL and a file on disk, and `src/seo/routeMeta.ts` keys this
   route's <title> and canonical off it. Three ways to get it wrong, all of
   them quiet:

     · A DUPLICATE writes two routes to one file. The second pre-render
       overwrites the first, so one workshop's detail page silently becomes
       another's — with the wrong canonical, which is the one error a crawler
       acts on.
     · AN EMPTY slug resolves to `/workshops/`, which is the index route with a
       trailing slash, not a detail page.
     · A SLUG WITH A SLASH, A SPACE OR AN UPPERCASE LETTER produces a path that
       does not match the file the pre-render wrote, or that Firebase
       canonicalizes before it consults a rewrite (the trap recorded at the
       write step in prerender.mjs).

   Checked at MODULE LOAD rather than in a test, because the pre-render imports
   this module: a bad slug fails `npm run build` (the D-071 mechanism), which is
   the only place that catches it before it ships. This is the same shape as the
   two-key invariant in `src/utils/workshopImages.ts`.
   -------------------------------------------------------------------------- */
const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
/* "YYYY-MM" or "YYYY" (D-092). Checked here for the same reason the slug is:
   the value is not just displayed, it ORDERS the route and is rendered into
   every detail page's meta description. A typo'd month is indistinguishable
   from a real one downstream, and `monthLabel` would render "undefined 2023"
   for anything this rejects rather than failing. */
const WORKSHOP_DATE_RE = /^\d{4}(?:-(?:0[1-9]|1[0-2]))?$/;
const seenSlugs = new Map<string, string>();
for (const workshop of workshops) {
	if (!WORKSHOP_DATE_RE.test(workshop.date)) {
		throw new Error(
			`content: workshop "${workshop.id}" has date ${JSON.stringify(workshop.date)}, which is ` +
				"neither YYYY-MM nor a bare YYYY. The value orders /workshops and is rendered into that " +
				"entry's detail-page meta description, so a malformed one is a wrong page rather than a wrong label."
		);
	}
	if (!SLUG_RE.test(workshop.slug)) {
		throw new Error(
			`content: workshop "${workshop.id}" has slug ${JSON.stringify(workshop.slug)}, which is not ` +
				"lower-case kebab-case. It becomes the URL /workshops/<slug> and the filename the " +
				"pre-render writes, so anything else produces a route that does not resolve to its own page."
		);
	}
	const owner = seenSlugs.get(workshop.slug);
	if (owner !== undefined) {
		throw new Error(
			`content: workshops "${owner}" and "${workshop.id}" both use the slug "${workshop.slug}". ` +
				"Two routes would pre-render to one file and the second would overwrite the first."
		);
	}
	seenSlugs.set(workshop.slug, workshop.id);
}

/**
 * A workshop by its slug, or undefined.
 *
 * The single lookup the detail route uses, so "which entry is this URL about?"
 * is answered in one place rather than by a `.find()` in a component.
 */
export const workshopBySlug = (slug: string): Workshop | undefined =>
	workshops.find((workshop: Workshop) => workshop.slug === slug);

/**
 * Letters of appreciation — the third Recognition section (D-106).
 *
 * DECLARED LAST IN THIS FILE ON PURPOSE, though it belongs beside `achievements`
 * by subject. The validation below reads `workshops`, which is a `const`
 * declared above it, and a reference from higher in the module would hit the
 * temporal dead zone at load — i.e. it would fail the build for a reason that
 * has nothing to do with the data. The alternative was to run the check inside a
 * function nobody calls, which is the shape of a gate that reports nothing.
 */
export const appreciations: Appreciation[] = (appreciationsJson as ContentFile<Appreciation[]>).data;

/* --------------------------------------------------------------------------
   EVERY `workshopReference` MUST NAME A WORKSHOP (D-106).
   --------------------------------------------------------------------------
   The Recognition card renders the work's title, host and dates FROM the
   workshop record and links to `/workshops/<slug>`. A reference that resolves
   to nothing does not throw at render — `find` returns undefined and the
   optional chain below it renders a card with no context and no link, which
   looks like a design choice rather than a typo. That is D-065's shape: the
   thing that fails is invisible, so it has to be made loud somewhere.

   Checked at MODULE LOAD, which fails `npm run build` because the pre-render
   imports this module — the same mechanism as the slug checks above.
   -------------------------------------------------------------------------- */
for (const appreciation of appreciations) {
	if (!workshops.some((workshop: Workshop) => workshop.id === appreciation.workshopReference)) {
		throw new Error(
			`appreciations.json: "${appreciation.id}" references workshop ` +
				`"${appreciation.workshopReference}", which workshops.json does not carry. ` +
				"The Recognition card takes the work's title, host and dates from that record and links to " +
				"its detail route, so an unresolved reference renders a card with no context and no link."
		);
	}
}
